import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { AuthService } from '../../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HTTP_STATUS_CODES, MAX_PHONE_DIGITS, OTP_LENGTH } from 'src/constants'
import { ViewChildren, QueryList, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { OtpVerificationResponse } from 'src/app/interfaces/responses/models'; // Assuming you have this interface

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  reactiveForm!: FormGroup
  protected maxPhoneDigits = MAX_PHONE_DIGITS;
  protected otpLength = OTP_LENGTH;
  protected otp_requested: boolean = false;
  protected is_loading: boolean = false;
  protected is_checked: boolean = false;
  protected is_authorized: boolean = true;
  protected otp_error: boolean = false;
  protected max_attempts_reached: boolean = false;
  protected phone_number_error: boolean = false;
  protected terms_and_privacy_policy_error: boolean = false;
  protected unexpected_error: boolean = false;
  protected otpParts = ['otp1', 'otp2', 'otp3', 'otp4', 'otp5', 'otp6'];
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  private state_token: string = "";
  private subscriptions: Subscription[] = [];

  isMobile: boolean = false;
  mode: string = "login";
  role: string | undefined = undefined;
  headerLabel: string = "Login";
  formLabel: string = "Enter the Mobile Number you have registered with us for this platform";


  constructor(
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private localStorage: LocalStorageService,
    private screenSizeService: ScreenSizeService,
    private route: Router
  ) {}

  ngOnInit() {
    this.role = this.activatedRoute.snapshot.data['role'];

    this.activatedRoute.queryParams.subscribe(params => {
      this.mode = params['mode'];
      this.updateLabels();
    });

    this.loadValidators();
    this.subscribePhoneNumberChanges();

    this.screenSizeService.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
    });

    this.get_phone_number?.valueChanges.subscribe(value => {
      if (value && value.toString().length === 10) {
        this.phone_number_error = false;
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  protected get get_phone_number() { return this.reactiveForm.get('phone_number') }

  updateLabels() {
    if (this.mode === 'login') {
      this.headerLabel = 'Login';
      this.formLabel = 'Enter the Mobile Number you have registered with us for this platform';
    } else if (this.mode === 'register') {
      this.headerLabel = 'Registration';
      this.formLabel = 'Enter the Mobile Number you want to Register with us for this platform';
    }
  }

  private loadValidators() {
    this.reactiveForm = new FormGroup({
      phone_number: new FormControl(null, [
        Validators.required,
        Validators.pattern('^[0-9]{10}$')
      ]),
    });
    for (let i = 1; i <= OTP_LENGTH; i++) {
      this.reactiveForm.addControl(`otp${i}`, new FormControl(null, Validators.required));
    }
  }

  protected onOtpInputKeyUp(event: KeyboardEvent, nextInputIndex: number, prevInputIndex: number) {
    if (event.key !== 'Backspace' && event.key !== "Tab") {
      this.otp_error = false
      if ((<HTMLInputElement>event.target).value && nextInputIndex !== -1) {
        this.otpInputs.toArray()[nextInputIndex].nativeElement.focus();
      }
    } else {
      if (prevInputIndex !== -1) {
        this.otpInputs.toArray()[prevInputIndex].nativeElement.focus();
      }
    }
  }

  private setRequestOtpState() {
    this.is_loading = false;
    this.otp_requested = true;
    this.resetOtp();
  }

  private setRequestPhoneState() {
    this.max_attempts_reached = false;
    this.is_loading = false;
    this.otp_requested = false;
    this.otp_error = false;
    this.unexpected_error = false;
    this.resetOtp();
    this.get_phone_number!.reset();
  }

  private resetOtp() {
    this.otpParts.forEach(name => this.reactiveForm.get(name)?.reset());
  }

  private subscribePhoneNumberChanges() {
    this.subscriptions.push(
      this.get_phone_number!.valueChanges.subscribe((newValue) => {
        if (newValue && newValue.toString().length !== 0) {
          this.is_authorized = true;
          this.max_attempts_reached = false;
        }
      })
    );
  }

  private processError(error: any) {
    this.setRequestPhoneState();
    if (error.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
      this.is_authorized = false;
    } else {
      this.unexpected_error = true;
    }
  }

  protected onOtpSubmit() {
    let otpParts = this.otpParts.map((name) => this.reactiveForm.get(name)?.value);
    this.is_loading = true;
    let otp: string = otpParts.join('');

    this.subscriptions.push(
      this.auth.verifyOtp(this.state_token, otp, this.role).subscribe({
        next: (res) => {
          if (res.status === "OTP_RESTRICTED") {
            this.setRequestPhoneState();
            this.max_attempts_reached = true;
            return;
          }
          if (res.status === 'OTP_FAILED') {
            this.setRequestOtpState();
            this.otp_error = true;
            return;
          }
          if (res.status === "SUCCESS") {
            this.handleSuccessfulLogin(res);
          } else {
            this.setRequestOtpState();
            this.otp_error = true;
          }
        },
        error: (error) => {
          this.processError(error);
        }
      })
    );
  }

  private async handleSuccessfulLogin(response: OtpVerificationResponse) {

    await this.localStorage.setToken(response.session_token);
    this.localStorage.setRole(response.role);
    this.localStorage.setUserId(response.user_id);
    this.localStorage.setBrand(response.name);
    this.localStorage.setLogoUrl(response.logo_url);
    this.localStorage.setPermission(response.buy, response.sell);


    let redirectUrl = '/'; // Default for roles like Consumer
    switch (response.role) {
      case 'Pending':
        redirectUrl = this.isMobile ? '/pending-registration-mobile' : '/pending';
        break;
      case 'guest':
        redirectUrl = '/register';
        break;
      case 'Utility':
        redirectUrl = '/homepage'; // Set homepage as default after login
        break;
      case 'IntegrationPartner':
        redirectUrl = '/partner-dashboard';
        break;
      case 'ServiceProvider':
        redirectUrl = '/provider-dash';
        break;
      case 'SuperAdmin':
        redirectUrl = '/admin-dash/dashboard';
        break;
    }
    
    this.route.navigateByUrl(redirectUrl);
  }

  protected onPhoneNumberSubmit() {
    this.is_loading = true;
    this.phone_number_error = false;
    this.terms_and_privacy_policy_error = false;
    let phoneNumber: number = this.get_phone_number?.value;

    if (phoneNumber.toString().length !== 10) {
      this.phone_number_error = true;
      this.is_loading = false;
      return;
    }

    if (this.isMobile && !this.is_checked) {
      this.terms_and_privacy_policy_error = true;
      this.is_loading = false;
      return;
    }
    
    this.subscriptions.push(
      this.auth.requestOtpForUser(phoneNumber).subscribe({
        next: (res) => {
          if (res.status === "RESTRICTED") {
            this.setRequestPhoneState();
            this.max_attempts_reached = true;
            return;
          }
          this.setRequestOtpState();
          this.state_token = res.state_token;
          this.localStorage.setPhoneNumber(phoneNumber);
        },
        error: (error) => {
          this.processError(error);
        },
        complete: () => {
          this.is_loading = false;
        }
      })
    );
  }

  protected onCheckboxClick(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.is_checked = checkbox.checked;
    this.terms_and_privacy_policy_error = !this.is_checked;
  }

  navigateTo(path: string) {
    this.route.navigate([path]);
  }
}